import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth0Data } from '../../context/Auth0DataContext';
import apiClient from '../../lib/api';
import { format, parseISO } from 'date-fns';
import UserTeamBadges from './UserTeamBadges';
import UserTeamsModal from './UserTeamsModal';
import { Users } from 'lucide-react';

const {
  FiUserCheck, FiPlus, FiEdit, FiTrash2, FiSave, FiX, FiShield, FiUser,
  FiEye, FiEyeOff, FiKey, FiRefreshCw, FiAlertTriangle, FiCheck, FiMail
} = FiIcons;

const UserManagement = () => {
  const { user: currentUser } = useAuth0Data();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [fueldashRoles, setFueldashRoles] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: ''
  });

  // Team management state
  const [allTeams, setAllTeams] = useState([]);
  const [userTeamsCache, setUserTeamsCache] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [showTeamsModal, setShowTeamsModal] = useState(false);
  const [loadingUserTeams, setLoadingUserTeams] = useState({});

  // Load users and check admin status
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 Loading users from Auth0...');

      // Get current user from Auth0
      if (!currentUser) {
        throw new Error('Not authenticated');
      }

      // Load users, teams, and roles in parallel
      const [auth0Users, teamsData, allRoles] = await Promise.all([
        apiClient.getAllAuth0Users(),
        apiClient.getAllTeamsAdmin().catch(err => {
          console.warn('Failed to load teams (user may not be admin):', err);
          return [];
        }),
        apiClient.getAllAuth0Roles().catch(err => {
          console.warn('Failed to load roles:', err);
          return [];
        })
      ]);

      const filteredRoles = allRoles
        .filter(r => r.name.startsWith('fueldash:'))
        .sort((a, b) => a.name.localeCompare(b.name));
      setFueldashRoles(filteredRoles);

      // Find current user's role
      const currentProfile = auth0Users.find(p => p.id === currentUser.sub);
      setCurrentUserRole(currentProfile?.role || null);
      setUsers(auth0Users || []);
      setAllTeams(teamsData || []);

      console.log(`✅ Loaded ${auth0Users.length} users and ${teamsData?.length || 0} teams successfully`);
    } catch (err) {
      console.error('❌ Error loading users:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const createUser = async () => {
    if (!formData.name.trim() || !formData.email.trim() || !formData.password.trim()) {
      setActionMessage('❌ Please fill in all required fields');
      setTimeout(() => setActionMessage(''), 5000);
      return;
    }

    if (formData.password.length < 6) {
      setActionMessage('❌ Password must be at least 6 characters long');
      setTimeout(() => setActionMessage(''), 5000);
      return;
    }

    // Check if email already exists
    const existingUser = users.find(u => u.email.toLowerCase() === formData.email.toLowerCase());
    if (existingUser) {
      setActionMessage('❌ A user with this email already exists');
      setTimeout(() => setActionMessage(''), 5000);
      return;
    }

    setActionLoading(true);
    setActionMessage('🔄 Creating user in Auth0...');

    try {
      console.log('➕ Creating new user:', { email: formData.email, name: formData.name, role: formData.role });

      // Create user via Auth0 Management API
      const newUser = await apiClient.createAuth0User({
        email: formData.email.trim(),
        password: formData.password,
        name: formData.name.trim(),
        role: formData.role
      });

      console.log('✅ User created successfully:', newUser.id);

      // Optimistically add new user to local state instead of re-fetching
      // (Auth0 has eventual consistency, so re-fetch may not include new user yet)
      setUsers(prevUsers => [...prevUsers, {
        id: newUser.id,
        email: formData.email.trim(),
        name: formData.name.trim(),
        role: formData.role,
        email_verified: false,
        created_at: new Date().toISOString()
      }]);

      // Reset form
      setFormData({
        name: '',
        email: '',
        password: '',
        role: ''
      });
      setIsAdding(false);
      setActionMessage('✅ User created successfully!');
      setTimeout(() => setActionMessage(''), 3000);

    } catch (error) {
      console.error('❌ Error creating user:', error);
      setActionMessage(`❌ Error: ${error.message}`);
      setTimeout(() => setActionMessage(''), 5000);
    } finally {
      setActionLoading(false);
    }
  };

  const updateUser = async () => {
    if (!formData.name.trim() || !formData.email.trim()) {
      setActionMessage('❌ Name and email are required');
      setTimeout(() => setActionMessage(''), 5000);
      return;
    }

    setActionLoading(true);
    setActionMessage('🔄 Updating user...');

    try {
      // Update user via Auth0 Management API
      const updatedUser = await apiClient.updateAuth0User(editingId, {
        name: formData.name.trim(),
        email: formData.email.trim(),
        role: formData.role
      });

      // Optimistically update local state instead of re-fetching
      // (Auth0 has eventual consistency, so re-fetch may return stale data)
      setUsers(prevUsers => prevUsers.map(user =>
        user.id === editingId
          ? { ...user, name: formData.name.trim(), email: formData.email.trim(), role: formData.role }
          : user
      ));

      setEditingId(null);
      setFormData({ name: '', email: '', password: '', role: '' });
      setActionMessage('✅ User updated successfully!');
      setTimeout(() => setActionMessage(''), 3000);

    } catch (error) {
      console.error('❌ Error updating user:', error);
      setActionMessage(`❌ Error: ${error.message}`);
      setTimeout(() => setActionMessage(''), 5000);
    } finally {
      setActionLoading(false);
    }
  };

  const resetUserPassword = async (userId) => {
    if (userId === currentUser?.sub) {
      setActionMessage('❌ You cannot reset your own password');
      setTimeout(() => setActionMessage(''), 5000);
      return;
    }

    setActionLoading(true);
    setActionMessage('🔄 Sending password reset email...');

    try {
      const targetUser = users.find(u => u.id === userId);
      if (!targetUser) {
        throw new Error('User not found');
      }

      // Send password reset email via Auth0 Management API
      await apiClient.resetUserPassword(userId);

      setActionMessage(`✅ Password reset email sent to ${targetUser.email}`);
      setTimeout(() => setActionMessage(''), 5000);

    } catch (error) {
      console.error('❌ Password reset error:', error);
      setActionMessage(`❌ Error: ${error.message}`);
      setTimeout(() => setActionMessage(''), 5000);
    } finally {
      setActionLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    if (userId === currentUser?.sub) {
      setActionMessage('❌ You cannot delete your own account');
      setTimeout(() => setActionMessage(''), 5000);
      return;
    }

    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    setActionLoading(true);
    setActionMessage('🔄 Deleting user...');

    try {
      // Delete user via Auth0 Management API (also deletes from local DB)
      await apiClient.deleteAuth0User(userId);

      setActionMessage('✅ User deleted successfully!');
      await loadUsers();
      setTimeout(() => setActionMessage(''), 3000);

    } catch (error) {
      console.error('❌ Error deleting user:', error);
      setActionMessage(`❌ Error: ${error.message}`);
      setTimeout(() => setActionMessage(''), 5000);
    } finally {
      setActionLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingId(user.id);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role
    });
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData({ name: '', email: '', password: '', role: '' });
    setActionMessage('');
  };

  const getRoleLabel = (role) => role ? role.replace('fueldash:', '') : 'no role';

  const getRoleIcon = (role) => {
    if (role === 'fueldash:admin') return FiShield;
    return FiUser;
  };

  const getRoleColor = (role) => {
    if (role === 'fueldash:admin') return 'text-red-600 bg-red-100';
    if (role === 'fueldash:user') return 'text-blue-600 bg-blue-100';
    return 'text-gray-600 bg-gray-100';
  };

  // Load teams for a specific user (lazy loading)
  const loadUserTeams = async (userId) => {
    if (userTeamsCache[userId]) {
      return userTeamsCache[userId];
    }

    setLoadingUserTeams(prev => ({ ...prev, [userId]: true }));

    try {
      const teams = await apiClient.getUserTeams(userId);
      setUserTeamsCache(prev => ({ ...prev, [userId]: teams }));
      return teams;
    } catch (err) {
      console.error(`Error loading teams for user ${userId}:`, err);
      return [];
    } finally {
      setLoadingUserTeams(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Open the teams modal for a user
  const handleManageTeams = async (user) => {
    setSelectedUser(user);
    await loadUserTeams(user.id);
    setShowTeamsModal(true);
  };

  // Handle when teams are changed in the modal
  const handleTeamsChanged = async () => {
    if (selectedUser) {
      // Clear cache and reload
      setUserTeamsCache(prev => {
        const newCache = { ...prev };
        delete newCache[selectedUser.id];
        return newCache;
      });
      await loadUserTeams(selectedUser.id);
    }
  };

  // Close the teams modal
  const handleCloseTeamsModal = () => {
    setShowTeamsModal(false);
    setSelectedUser(null);
  };

  // Show loading state
  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
      >
        <div className="flex items-center justify-center py-12">
          <SafeIcon icon={FiRefreshCw} className="w-8 h-8 animate-spin text-primary-600 mr-3" />
          <span className="text-gray-600">Loading users...</span>
        </div>
      </motion.div>
    );
  }

  // Show error state
  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
      >
        <div className="flex items-center justify-center py-12 text-red-600">
          <SafeIcon icon={FiAlertTriangle} className="w-8 h-8 mr-3" />
          <div className="text-center">
            <p className="font-semibold">Error loading users</p>
            <p className="text-sm text-gray-600 mt-2">{error}</p>
            <button
              onClick={loadUsers}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2" />
              Retry
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  // Check admin access
  if (currentUserRole !== 'fueldash:admin' && users.length > 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
      >
        <div className="flex items-center justify-center py-12 text-orange-600">
          <SafeIcon icon={FiShield} className="w-8 h-8 mr-3" />
          <div className="text-center">
            <p className="font-semibold">Admin Access Required</p>
            <p className="text-sm text-gray-600">You need admin privileges to manage users</p>
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg text-left">
              <h4 className="font-medium text-blue-900 mb-2">Current Status</h4>
              <div className="text-sm text-blue-800">
                <p>• Signed in as: <strong>{currentUser?.email}</strong></p>
                <p>• Your role: <strong>{currentUserRole || 'none'}</strong></p>
                <p>• Admin access: <strong className="text-red-600">No</strong></p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  const adminCount = users.filter(user => user.role === 'fueldash:admin').length;
  const userCount = users.filter(user => user.role === 'fueldash:user').length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <SafeIcon icon={FiUserCheck} className="w-5 h-5 text-purple-600" />
          </div>
          <div className="ml-3">
            <h3 className="text-xl font-bold text-gray-900">User Management</h3>
            <p className="text-sm text-gray-500">Manage system users and permissions</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {actionMessage && (
            <div className={`flex items-center px-3 py-1 rounded-lg text-sm font-medium ${
              actionMessage.includes('❌') || actionMessage.includes('Error')
                ? 'bg-red-100 text-red-800'
                : actionMessage.includes('✅') || actionMessage.includes('successfully')
                ? 'bg-green-100 text-green-800'
                : 'bg-blue-100 text-blue-800'
            }`}>
              {actionLoading && <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2 animate-spin" />}
              {actionMessage.includes('✅') && <SafeIcon icon={FiCheck} className="w-4 h-4 mr-2" />}
              {actionMessage.includes('❌') && <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 mr-2" />}
              <span>{actionMessage}</span>
            </div>
          )}

          <button
            onClick={() => setIsAdding(true)}
            disabled={actionLoading}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
            Add User
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <div className="text-center p-4 bg-purple-50 rounded-lg">
          <p className="text-sm text-gray-600">Total Users</p>
          <p className="text-xl font-bold text-purple-600">{users.length}</p>
        </div>
        <div className="text-center p-4 bg-red-50 rounded-lg">
          <p className="text-sm text-gray-600">Admins</p>
          <p className="text-xl font-bold text-red-600">{adminCount}</p>
        </div>
        <div className="text-center p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-gray-600">Regular Users</p>
          <p className="text-xl font-bold text-blue-600">{userCount}</p>
        </div>
      </div>

      {/* Add User Form */}
      {isAdding && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mb-6 p-4 bg-gray-50 rounded-lg border"
        >
          <h4 className="font-semibold text-gray-900 mb-4">Add New User</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Enter full name"
                disabled={actionLoading}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Enter email address"
                disabled={actionLoading}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter password (min 6 characters)"
                  minLength={6}
                  disabled={actionLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                disabled={actionLoading}
              >
                <option value="">— select role —</option>
                {fueldashRoles.map(r => (
                  <option key={r.id} value={r.name}>{getRoleLabel(r.name)}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <button
              onClick={createUser}
              disabled={actionLoading || !formData.name.trim() || !formData.email.trim() || !formData.password.trim()}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <SafeIcon icon={actionLoading ? FiRefreshCw : FiSave} className={`w-4 h-4 mr-2 ${actionLoading ? 'animate-spin' : ''}`} />
              {actionLoading ? 'Creating...' : 'Create User'}
            </button>
            <button
              onClick={handleCancel}
              disabled={actionLoading}
              className="flex items-center px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
            >
              <SafeIcon icon={FiX} className="w-4 h-4 mr-2" />
              Cancel
            </button>
          </div>
        </motion.div>
      )}

      {/* Users List */}
      <div className="space-y-4">
        <AnimatePresence>
          {users.map((user, index) => (
            <motion.div
              key={user.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ delay: index * 0.05 }}
              className="border border-gray-200 rounded-lg p-4"
            >
              {editingId === user.id ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        disabled={actionLoading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        disabled={actionLoading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                      <select
                        value={formData.role || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        disabled={actionLoading}
                      >
                        <option value="">— select role —</option>
                        {fueldashRoles.map(r => (
                          <option key={r.id} value={r.name}>{getRoleLabel(r.name)}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={updateUser}
                      disabled={actionLoading}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400"
                    >
                      <SafeIcon icon={actionLoading ? FiRefreshCw : FiSave} className={`w-4 h-4 mr-2 ${actionLoading ? 'animate-spin' : ''}`} />
                      {actionLoading ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={actionLoading}
                      className="flex items-center px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                    >
                      <SafeIcon icon={FiX} className="w-4 h-4 mr-2" />
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getRoleColor(user.role)}`}>
                      <SafeIcon icon={getRoleIcon(user.role)} className="w-6 h-6" />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h4 className="font-semibold text-gray-900">{user.name}</h4>
                        {user.id === currentUser?.id && (
                          <span className="text-xs bg-primary-100 text-primary-800 px-2 py-1 rounded-full">
                            You
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-1">{user.email}</p>
                      <div className="flex items-center gap-4 text-xs text-gray-500 mb-2">
                        <span>Created: {format(parseISO(user.created_at), 'MMM dd, yyyy')}</span>
                        <span className="px-2 py-1 rounded-full bg-green-100 text-green-800">
                          Verified
                        </span>
                      </div>
                      {/* Team Badges */}
                      <UserTeamBadges
                        teams={userTeamsCache[user.id]}
                        loading={loadingUserTeams[user.id]}
                        onClick={() => handleManageTeams(user)}
                      />
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getRoleColor(user.role)}`}>
                        {getRoleLabel(user.role)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleManageTeams(user)}
                      disabled={actionLoading}
                      className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg transition-colors disabled:opacity-50"
                      title="Manage Teams"
                    >
                      <Users className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(user)}
                      disabled={actionLoading}
                      className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors disabled:opacity-50"
                      title="Edit User"
                    >
                      <SafeIcon icon={FiEdit} className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => resetUserPassword(user.id)}
                      disabled={actionLoading || user.id === currentUser?.id}
                      className="p-2 text-orange-600 hover:bg-orange-100 rounded-lg transition-colors disabled:opacity-50"
                      title={user.id === currentUser?.id ? "Cannot reset your own password" : "Reset Password"}
                    >
                      <SafeIcon icon={FiKey} className="w-4 h-4" />
                    </button>
                    {user.id !== currentUser?.id && (
                      <button
                        onClick={() => deleteUser(user.id)}
                        disabled={actionLoading}
                        className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
                        title="Delete User"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {users.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <SafeIcon icon={FiUserCheck} className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No users found.</p>
        </div>
      )}

      {/* Teams Modal */}
      <UserTeamsModal
        isOpen={showTeamsModal}
        onClose={handleCloseTeamsModal}
        user={selectedUser}
        allTeams={allTeams}
        userTeams={selectedUser ? (userTeamsCache[selectedUser.id] || []) : []}
        onTeamsChanged={handleTeamsChanged}
      />
    </motion.div>
  );
};

export default UserManagement;